// src/pages/RestaurantOwner/Tables.jsx - FIXED QR LIMIT ISSUE

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2, QrCode, Download, ExternalLink, Loader, Edit } from 'lucide-react';
import StorageService from '../../services/StorageService';
import { doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import QRCodeLib from 'qrcode';

const Tables = ({ restaurantId }) => {
  const [tables, setTables] = useState([]);
  const [qrLimit, setQrLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableName, setTableName] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [packageName, setPackageName] = useState('Basic');

  useEffect(() => {
    loadTables();
  }, [restaurantId]);

  // ✅ FIXED: Properly fetch QR limit from restaurant's package
  const loadTables = async () => {
    try {
      setLoading(true);
       
      
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      if (restaurant) {
        setTables(restaurant.tables || []);
        setPackageName(restaurant.package || 'Basic');
        
         
        
        // ✅ FIX: Get ALL data including packages
        const data = await StorageService.getData();
         
        
        // ✅ FIX: Find the correct package by name (case-insensitive)
        const pkg = data.packages.find(p => 
          p.name.toLowerCase() === (restaurant.package || 'basic').toLowerCase()
        );
        
         
        
        if (pkg && pkg.qrLimit) {
          setQrLimit(pkg.qrLimit);
           
        } else {
          // ✅ FIX: Default fallback based on package name if package not found
          const defaultLimits = {
            'basic': 10,
            'premium': 30,
            'advanced': 50
          };
          const fallbackLimit = defaultLimits[restaurant.package.toLowerCase()] || 10;
          setQrLimit(fallbackLimit);
           
        }
      }
    } catch (error) {
      console.error('❌ Error loading tables:', error);
      // ✅ FIX: Set a safe default if everything fails
      setQrLimit(10);
    } finally {
      setLoading(false);
    }
  };

  // ✅ OPTIMIZED: Direct Firestore write + optimistic UI update
  const handleAddTable = async (e) => {
    e.preventDefault();
    
    if (tables.length >= qrLimit) {
      alert(`You have reached the maximum limit of ${qrLimit} tables for your ${packageName} package. Please upgrade to add more tables.`);
      return;
    }

    if (!tableName.trim()) {
      alert('Please enter a table name');
      return;
    }

    try {
      setSaving(true);

      const newTable = {
        id: `table_${Date.now()}`,
        name: tableName.trim(),
        createdAt: new Date().toISOString()
      };

      // ✅ OPTIMISTIC UPDATE - Add to UI immediately
      setTables(prevTables => [...prevTables, newTable]);
      setTableName('');
      setShowAddModal(false);

      // ✅ Save to Firestore in background
      const tableRef = doc(db, 'restaurants', restaurantId, 'tables', newTable.id);
      await setDoc(tableRef, newTable);

      // ✅ Clear cache
      StorageService.clearCache();

       
    } catch (error) {
      console.error('❌ Error adding table:', error);
      alert('Failed to add table. Please try again.');
      // ✅ Reload on error
      await loadTables();
    } finally {
      setSaving(false);
    }
  };

  // ✅ NEW: Edit table handler
  const handleEditTable = async (e) => {
    e.preventDefault();
    
    if (!tableName.trim()) {
      alert('Please enter a table name');
      return;
    }

    if (!editingTable) return;

    try {
      setSaving(true);

      const updatedTable = {
        ...editingTable,
        name: tableName.trim(),
        updatedAt: new Date().toISOString()
      };

      // ✅ OPTIMISTIC UPDATE - Update UI immediately
      setTables(prevTables => 
        prevTables.map(t => t.id === editingTable.id ? updatedTable : t)
      );
      setTableName('');
      setEditingTable(null);
      setShowEditModal(false);

      // ✅ Save to Firestore in background
      const tableRef = doc(db, 'restaurants', restaurantId, 'tables', editingTable.id);
      await updateDoc(tableRef, {
        name: tableName.trim(),
        updatedAt: new Date().toISOString()
      });

      // ✅ Clear cache
      StorageService.clearCache();

       
    } catch (error) {
      console.error('❌ Error updating table:', error);
      alert('Failed to update table. Please try again.');
      // ✅ Reload on error
      await loadTables();
    } finally {
      setSaving(false);
    }
  };

  // ✅ OPTIMIZED: Direct Firestore delete + optimistic UI update
  const handleDeleteTable = async (tableId) => {
    if (window.confirm('Are you sure you want to delete this table? This will affect orders associated with this table.')) {
      try {
        setSaving(true);

        // ✅ OPTIMISTIC UPDATE - Remove from UI immediately
        setTables(prevTables => prevTables.filter(t => t.id !== tableId));

        // ✅ Delete from Firestore in background
        const tableRef = doc(db, 'restaurants', restaurantId, 'tables', tableId);
        await deleteDoc(tableRef);

        // ✅ Clear cache
        StorageService.clearCache();

         
      } catch (error) {
        console.error('❌ Error deleting table:', error);
        alert('Failed to delete table. Please try again.');
        // ✅ Reload on error
        await loadTables();
      } finally {
        setSaving(false);
      }
    }
  };

  // ✅ MEMOIZED: Generate QR code (expensive operation)
  const generateQRCode = useCallback(async (table) => {
    const url = `${window.location.origin}/customer/${restaurantId}/${table.id}`;
    try {
      const qrImage = await QRCodeLib.toDataURL(url, {
        width: 512,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrDataUrl(qrImage);
      setSelectedTable(table);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code');
    }
  }, [restaurantId]);

  // ✅ OPTIMIZED: Download QR code
  const downloadQRCode = useCallback(() => {
    if (!selectedTable || !qrDataUrl) return;
    
    const link = document.createElement('a');
    link.download = `QR_${selectedTable.name.replace(/\s+/g, '_')}.png`;
    link.href = qrDataUrl;
    link.click();
  }, [selectedTable, qrDataUrl]);

  // ✅ OPTIMIZED: Test URL handler
  const handleTestUrl = useCallback((table) => {
    const url = `${window.location.origin}/customer/${restaurantId}/${table.id}`;
    window.open(url, '_blank');
  }, [restaurantId]);

  // ✅ OPTIMIZED: Modal handlers
  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setTableName('');
    setEditingTable(null);
  }, []);

  const openEditModal = useCallback((table) => {
    setEditingTable(table);
    setTableName(table.name);
    setShowEditModal(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setTableName('');
    setEditingTable(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setTableName('');
    setEditingTable(null);
  }, []);

  const closeQRModal = useCallback(() => {
    setShowQRModal(false);
    setSelectedTable(null);
    setQrDataUrl('');
  }, []);

  const handleTableNameChange = useCallback((e) => {
    setTableName(e.target.value);
  }, []);

  // ✅ MEMOIZED: Check if at limit
  const isAtLimit = useMemo(() => tables.length >= qrLimit, [tables.length, qrLimit]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading tables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">QR Tables Management</h1>
          <p className="text-gray-600 mt-1">
            {tables.length} / {qrLimit} tables used ({packageName} Plan)
          </p>
        </div>
        <button
          onClick={openAddModal}
          disabled={isAtLimit || saving}
          className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition ${
            isAtLimit || saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-orange-500 text-white hover:bg-orange-600'
          }`}
        >
          <Plus className="w-5 h-5" /> Add Table
        </button>
      </div>

      {/* Limit Warning */}
      {isAtLimit && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-medium">
            ⚠️ You have reached your table limit. Upgrade your package to add more tables.
          </p>
        </div>
      )}

      {/* Tables Grid */}
      {tables.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tables.map(table => (
            <div key={table.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">{table.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(table)}
                    disabled={saving}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
                    title="Edit table"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteTable(table.id)}
                    disabled={saving}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition"
                    title="Delete table"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <p className="truncate" title={table.id}>ID: {table.id}</p>
                <p>Created: {new Date(table.createdAt).toLocaleDateString()}</p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => generateQRCode(table)}
                  disabled={saving}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 disabled:opacity-50 transition"
                >
                  <QrCode className="w-4 h-4" /> View QR Code
                </button>
                <button
                  onClick={() => handleTestUrl(table)}
                  disabled={saving}
                  className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 flex items-center justify-center gap-2 disabled:opacity-50 transition"
                >
                  <ExternalLink className="w-4 h-4" /> Test URL
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl shadow-md">
          <QrCode className="w-20 h-20 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Tables Yet</h3>
          <p className="text-gray-500 mb-4">Create your first table to get started with QR ordering</p>
          <button
            onClick={openAddModal}
            disabled={saving}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 transition"
          >
            Add Your First Table
          </button>
        </div>
      )}

      {/* Add Table Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Add New Table</h2>
            <form onSubmit={handleAddTable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Table Name</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={handleTableNameChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., Table 1, VIP Table, Window Seat"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p>📊 {tables.length} / {qrLimit} tables used</p>
                <p className="text-xs mt-1">Each table gets a unique QR code for customer orders</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeAddModal}
                  disabled={saving}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
                >
                  {saving ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Table'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {showEditModal && editingTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Edit Table</h2>
            <form onSubmit={handleEditTable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Table Name</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={handleTableNameChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Table 1, VIP Table, Window Seat"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p className="text-xs">Table ID: {editingTable.id}</p>
                <p className="text-xs mt-1">Created: {new Date(editingTable.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={saving}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
                >
                  {saving ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Table'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedTable.name}</h2>
              <p className="text-gray-600">Scan this QR code to place an order</p>
            </div>
            
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white border-4 border-gray-200 rounded-xl">
                <img src={qrDataUrl} alt="QR Code" className="w-64 h-64" />
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-xs text-gray-600 mb-1">Customer URL:</p>
              <p className="text-sm font-mono text-gray-800 break-all">
                {window.location.origin}/customer/{restaurantId}/{selectedTable.id}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeQRModal}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition"
              >
                Close
              </button>
              <button
                onClick={downloadQRCode}
                className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 transition"
              >
                <Download className="w-5 h-5" /> Download QR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tables;